// Story BP-8: Admin Billing hook — consumer das RPCs SECURITY DEFINER
// (Phase 1 @Dara) + queries diretas em system_logs (telemetry) e
// admin_audit_log. Fail-fast em non-superadmin via RPC RLS.
//
// Pattern alinhado com hooks/useAdminUsers.tsx (supabase.rpc + useEffect +
// refreshKey), zero deps novas (sem TanStack Query).

import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import type { PlanTier } from '../lib/plans/types';

// ─── Types ────────────────────────────────────────────────────────

export interface AdminBillingUser {
  id: string;
  email: string;
  full_name: string | null;
  plan_tier: PlanTier;
  /** Resolvido via get_effective_plan() (considera grandfather + exempt). */
  effective_tier: PlanTier;
  grandfather_until: string | null;
  is_exempt: boolean;
  trial_ends_at: string | null;
  created_at: string;
  /** Subscription status (null se sem subscription). */
  subscription_status: string | null;
  /** Razão pela qual está em "Needs Attention" (null se não aplicável). */
  needs_attention_reason?: 'grandfather_expiring' | 'past_due' | null;
}

// NOTE: Dara Phase 1 estendeu `admin_audit_log` (mig 13 + mig 24) com +3
// colunas (before/after/reason). Shape real do schema:
//   id | admin_id | target_id | target_type | action | before | after |
//   reason | created_at
// target_type='user' pra overrides de plano/grandfather.
export interface AuditLogEntry {
  id: string;
  action: string;
  admin_id: string;
  target_id: string;
  target_type: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  reason: string | null;
  created_at: string;
}

export interface UpgradeIntentRawEvent {
  id: string;
  created_at: string;
  user_id: string | null;
  metadata: {
    feature?: string;
    tier_current?: string;
    tier_required?: string;
    cta_clicked?: boolean;
    [k: string]: unknown;
  } | null;
}

export interface UpgradeIntentAggregatedRow {
  feature: string;
  tier_current: string;
  tier_required: string;
  count: number;
  unique_users: number;
}

export type TelemetryMode = 'raw' | 'aggregated';

// ─── Helpers (shared) ────────────────────────────────────────────

// Resolve effective tier client-side via RPC get_effective_plan (um por user).
// Mantém paralelismo moderado pra não floodar o supabase.
async function resolveEffectiveTiers(
  userIds: string[]
): Promise<Record<string, PlanTier>> {
  const result: Record<string, PlanTier> = {};
  const CONCURRENCY = 8;
  for (let i = 0; i < userIds.length; i += CONCURRENCY) {
    const batch = userIds.slice(i, i + CONCURRENCY);
    const settled = await Promise.all(
      batch.map(async (id) => {
        const { data } = await supabase.rpc('get_effective_plan', { p_user_id: id });
        return [id, (data as PlanTier) ?? 'free'] as const;
      })
    );
    for (const [id, tier] of settled) result[id] = tier;
  }
  return result;
}

// ─── fetchNeedsAttention ─────────────────────────────────────────
// Client-side query: profiles + subscriptions. Filtros:
// - grandfather_until entre now() e now()+30d
// - OR subscriptions.status='past_due'

export async function fetchNeedsAttention(): Promise<{
  data: AdminBillingUser[];
  error: string | null;
}> {
  const nowIso = new Date().toISOString();
  const d30Iso = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();

  // Query 1: grandfather expirando
  const gfQuery = supabase
    .from('profiles')
    .select('id, email, full_name, plan_tier, grandfather_until, is_exempt, trial_ends_at, created_at')
    .gte('grandfather_until', nowIso)
    .lte('grandfather_until', d30Iso);

  // Query 2: past_due via subscriptions join
  const pdQuery = supabase
    .from('subscriptions')
    .select('user_id, status, profiles!inner(id, email, full_name, plan_tier, grandfather_until, is_exempt, trial_ends_at, created_at)')
    .eq('status', 'past_due');

  const [gfRes, pdRes] = await Promise.all([gfQuery, pdQuery]);

  if (gfRes.error || pdRes.error) {
    const msg = gfRes.error?.message || pdRes.error?.message || 'Query error';
    console.error('[useAdminBilling] fetchNeedsAttention error:', msg);
    return { data: [], error: msg };
  }

  type ProfileRow = {
    id: string;
    email: string;
    full_name: string | null;
    plan_tier: PlanTier;
    grandfather_until: string | null;
    is_exempt: boolean;
    trial_ends_at: string | null;
    created_at: string;
  };

  const merged = new Map<string, AdminBillingUser>();
  for (const p of (gfRes.data ?? []) as ProfileRow[]) {
    merged.set(p.id, {
      ...p,
      effective_tier: p.plan_tier,
      subscription_status: null,
      needs_attention_reason: 'grandfather_expiring',
    });
  }
  for (const row of (pdRes.data ?? []) as Array<{
    user_id: string;
    status: string;
    profiles: ProfileRow | ProfileRow[];
  }>) {
    const profile = Array.isArray(row.profiles) ? row.profiles[0] : row.profiles;
    if (!profile) continue;
    const existing = merged.get(profile.id);
    if (existing) {
      existing.subscription_status = row.status;
      // past_due prevalece sobre grandfather pra exibição
      existing.needs_attention_reason = 'past_due';
    } else {
      merged.set(profile.id, {
        ...profile,
        effective_tier: profile.plan_tier,
        subscription_status: row.status,
        needs_attention_reason: 'past_due',
      });
    }
  }

  const users = Array.from(merged.values());
  const effectiveTiers = await resolveEffectiveTiers(users.map(u => u.id));
  for (const u of users) {
    u.effective_tier = effectiveTiers[u.id] ?? u.plan_tier;
  }

  users.sort((a, b) => {
    // past_due primeiro, depois grandfather expiring por data mais próxima
    if (a.needs_attention_reason === 'past_due' && b.needs_attention_reason !== 'past_due') return -1;
    if (b.needs_attention_reason === 'past_due' && a.needs_attention_reason !== 'past_due') return 1;
    return (a.grandfather_until ?? '').localeCompare(b.grandfather_until ?? '');
  });

  return { data: users, error: null };
}

// ─── fetchAllUsers (paginated, client-side via profiles) ─────────

interface FetchAllUsersParams {
  search?: string | null;
  page?: number;
  perPage?: number;
}

export async function fetchAllBillingUsers({
  search = null,
  page = 1,
  perPage = 50,
}: FetchAllUsersParams): Promise<{
  data: { users: AdminBillingUser[]; total: number };
  error: string | null;
}> {
  const from = (page - 1) * perPage;
  const to = from + perPage - 1;

  let query = supabase
    .from('profiles')
    .select(
      'id, email, full_name, plan_tier, grandfather_until, is_exempt, trial_ends_at, created_at',
      { count: 'exact' }
    )
    .order('created_at', { ascending: false })
    .range(from, to);

  if (search && search.trim().length > 0) {
    const term = search.trim();
    // Busca por email OU id (prefix match é suficiente)
    query = query.or(`email.ilike.%${term}%,id.eq.${term}`);
  }

  const { data, count, error } = await query;
  if (error) {
    console.error('[useAdminBilling] fetchAllBillingUsers error:', error);
    return { data: { users: [], total: 0 }, error: error.message };
  }

  type Row = {
    id: string;
    email: string;
    full_name: string | null;
    plan_tier: PlanTier;
    grandfather_until: string | null;
    is_exempt: boolean;
    trial_ends_at: string | null;
    created_at: string;
  };

  const baseUsers: AdminBillingUser[] = ((data ?? []) as Row[]).map(r => ({
    ...r,
    effective_tier: r.plan_tier,
    subscription_status: null,
  }));

  const effectiveTiers = await resolveEffectiveTiers(baseUsers.map(u => u.id));
  for (const u of baseUsers) {
    u.effective_tier = effectiveTiers[u.id] ?? u.plan_tier;
  }

  return { data: { users: baseUsers, total: count ?? 0 }, error: null };
}

// ─── Mutations (delegated to SECURITY DEFINER RPCs) ──────────────

// RPC names alinhados com Dara mig 24: admin_set_plan_tier / admin_set_grandfather
// Auth gate via is_superadmin() + reason CHECK (>=10 chars) no DB.

export async function overridePlanTier(
  targetUserId: string,
  newTier: PlanTier,
  reason: string
): Promise<{ error: string | null }> {
  if (!reason || reason.trim().length < 10) {
    return { error: 'Reason must be at least 10 characters.' };
  }
  const { error } = await supabase.rpc('admin_set_plan_tier', {
    p_target_user_id: targetUserId,
    p_new_tier: newTier,
    p_reason: reason.trim(),
  });
  if (error) {
    console.error('[useAdminBilling] overridePlanTier error:', error);
    return { error: error.message };
  }
  return { error: null };
}

export async function overrideGrandfather(
  targetUserId: string,
  newExpiry: string | null,
  reason: string
): Promise<{ error: string | null }> {
  if (!reason || reason.trim().length < 10) {
    return { error: 'Reason must be at least 10 characters.' };
  }
  const { error } = await supabase.rpc('admin_set_grandfather', {
    p_target_user_id: targetUserId,
    p_new_until: newExpiry,
    p_reason: reason.trim(),
  });
  if (error) {
    console.error('[useAdminBilling] overrideGrandfather error:', error);
    return { error: error.message };
  }
  return { error: null };
}

// ─── fetchAuditLog ───────────────────────────────────────────────

interface FetchAuditLogParams {
  targetUserId?: string;
  actorId?: string;
  limit?: number;
}

export async function fetchAuditLog({
  targetUserId,
  actorId,
  limit = 10,
}: FetchAuditLogParams): Promise<{ data: AuditLogEntry[]; error: string | null }> {
  let query = supabase
    .from('admin_audit_log')
    .select('id, admin_id, target_id, target_type, action, before, after, reason, created_at')
    .order('created_at', { ascending: false })
    .limit(limit);

  if (targetUserId) {
    query = query.eq('target_type', 'user').eq('target_id', targetUserId);
  }
  if (actorId) query = query.eq('admin_id', actorId);

  const { data, error } = await query;
  if (error) {
    console.error('[useAdminBilling] fetchAuditLog error:', error);
    return { data: [], error: error.message };
  }
  return { data: (data as AuditLogEntry[]) ?? [], error: null };
}

// ─── fetchUpgradeTelemetry ───────────────────────────────────────

interface FetchTelemetryParams {
  mode: TelemetryMode;
  days?: number;
}

export async function fetchUpgradeTelemetry({
  mode,
  days = 30,
}: FetchTelemetryParams): Promise<{
  data: UpgradeIntentRawEvent[] | UpgradeIntentAggregatedRow[];
  error: string | null;
}> {
  if (mode === 'raw') {
    const { data, error } = await supabase
      .from('system_logs')
      .select('id, created_at, user_id, metadata')
      .eq('category', 'upgrade_intent')
      .order('created_at', { ascending: false })
      .limit(100);
    if (error) {
      console.error('[useAdminBilling] telemetry raw error:', error);
      return { data: [], error: error.message };
    }
    return { data: (data as UpgradeIntentRawEvent[]) ?? [], error: null };
  }

  // Aggregated mode — client-side GROUP BY sobre últimos N dias (Dara não
  // entregou RPC de aggregation dedicada; volume upgrade_intent é baixo
  // suficiente pra client-side agg ser aceitável no MVP).
  const sinceIso = new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();
  const { data, error } = await supabase
    .from('system_logs')
    .select('user_id, metadata')
    .eq('category', 'upgrade_intent')
    .gte('created_at', sinceIso);

  if (error) {
    console.error('[useAdminBilling] telemetry aggregated error:', error);
    return { data: [], error: error.message };
  }

  // GROUP BY feature/tier_current/tier_required com COUNT + COUNT DISTINCT user_id
  const groups = new Map<string, { row: UpgradeIntentAggregatedRow; users: Set<string> }>();
  for (const raw of (data ?? []) as Array<{ user_id: string | null; metadata: Record<string, unknown> | null }>) {
    const m = raw.metadata ?? {};
    const feature = String(m.feature ?? 'unknown');
    const tier_current = String(m.tier_current ?? 'unknown');
    const tier_required = String(m.tier_required ?? 'unknown');
    const key = `${feature}|${tier_current}|${tier_required}`;
    let g = groups.get(key);
    if (!g) {
      g = {
        row: { feature, tier_current, tier_required, count: 0, unique_users: 0 },
        users: new Set(),
      };
      groups.set(key, g);
    }
    g.row.count += 1;
    if (raw.user_id) g.users.add(raw.user_id);
  }

  const rows: UpgradeIntentAggregatedRow[] = [];
  for (const { row, users } of groups.values()) {
    rows.push({ ...row, unique_users: users.size });
  }
  rows.sort((a, b) => b.count - a.count);
  return { data: rows, error: null };
}

// ─── useNeedsAttention hook (list + realtime) ────────────────────

export function useNeedsAttention(): {
  users: AdminBillingUser[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const [users, setUsers] = useState<AdminBillingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchNeedsAttention().then(({ data, error: err }) => {
      if (cancelled) return;
      setUsers(data);
      setError(err);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  // Realtime: refresh list quando admin_audit_log muda (ação de outro admin)
  useEffect(() => {
    const channel = supabase
      .channel('admin-audit-log-changes')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'admin_audit_log' },
        () => refresh()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [refresh]);

  return { users, loading, error, refresh };
}

export function useAllBillingUsers(params: FetchAllUsersParams): {
  users: AdminBillingUser[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
} {
  const { search, page, perPage } = params;
  const [users, setUsers] = useState<AdminBillingUser[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey(k => k + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchAllBillingUsers({ search, page, perPage }).then(({ data, error: err }) => {
      if (cancelled) return;
      setUsers(data.users);
      setTotal(data.total);
      setError(err);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [search, page, perPage, refreshKey]);

  return { users, total, loading, error, refresh };
}
